import React, { useCallback, useState, useEffect } from "react";
import type { FinancialData } from "../../../shared/src/types/FinancialData";
import { useCreateMeetingMutation } from "../api/MeetingsApi";
import { useGetFamilyByIdQuery } from "../api/familyAPI";
import PopupModal from "./PopupModal";
import TaskTable from "./TaskTable";
import OpenTasksList from "./OpenTasksList";
import PdfDownloader from "../utils/generatePdf";
import SendEmailButton from "../utils/SendEmailButtun";
import NextMeetingForm from "./NextMeetingForm";

const financialFieldKeys: (keyof FinancialData)[] = [
  "total_income",
  "total_expenses",
  "current_debt_amount",
  "one_time_debt_repayment",
  "monthly_debt_repayment",
  "short_term_savings",
  "long_term_savings",
];

interface MeetingFormProps {
  userId?: string;
  autoFamilyId?: string;
}

function toNumber(value: string) {
  const n = Number(value);
  return isNaN(n) ? 0 : n;
}

const MeetingForm: React.FC<MeetingFormProps> = ({ userId, autoFamilyId }) => {
  const [createMeeting, { isLoading: saving }] = useCreateMeetingMutation();
  const [showPopup, setShowPopup] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [attendeesInput, setAttendeesInput] = useState("");

  const [meetingID, setMeetingID] = useState<string>("");
  const [currentMeeting, setCurrentMeeting] = useState<any>({
    id: '',
    family_id: '',
    mentor_id: '',
    location: '',
    attendees: [],
    meeting_number: 1,
    financialData: {
      id: '',
      total_income: 0,
      total_expenses: 0,
      current_debt_amount: 0,
      income_expense_difference: 0,
      one_time_debt_repayment: 0,
      monthly_debt_repayment: 0,
      difference_after_repayment: 0,
      short_term_savings: 0,
      long_term_savings: 0,
    },
  });
  
  const [formData, setFormData] = useState({
    newDate: "",
    newMeetingNumber: 0,
    newLocation: "",
    newAttendees: [] as string[],
    newFamilyId: '',
    newMentorId: '',
    details: '',
  });

  const { data: family } = useGetFamilyByIdQuery(userId || "", { skip: !userId });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      newFamilyId: autoFamilyId || family?.id || "",
    }));
  }, [autoFamilyId, family?.id, userId]);

  const [financialData, setFinancialData] = useState<Record<string, string>>({
    total_income: "0",
    total_expenses: "0",
    current_debt_amount: "0",
    one_time_debt_repayment: "0",
    monthly_debt_repayment: "0",
    short_term_savings: "0",
    long_term_savings: "0",
  });

  const handleFinancialFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    if (financialData[name] === "0") {
      setFinancialData((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFinancialBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value === "") {
      setFinancialData((prev) => ({ ...prev, [name]: "0" }));
    }
  };

  const handleFinancialChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (/^0\d+/.test(value)) return;
    if (/^\d*$/.test(value)) {
      setFinancialData((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  const handleBasicChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const tryUpdateAttendees = (raw: string) => {
    const updated = raw.split(",").map((s) => s.trim()).filter(Boolean);
    setFormData((prev) => ({ ...prev, newAttendees: updated }));
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: "ILS",
      minimumFractionDigits: 0,
    }).format(value);

  const handleSaveMeeting = async () => {
    setSaveSuccess(false);

    if (!userId || !autoFamilyId) {
      alert("שגיאה: חסרים מזהים נדרשים (מנטור או משפחה).");
      return;
    }

    try {
      if (!formData.newDate) throw new Error("נא להזין תאריך פגישה.");

      const meetingData = {
        date: formData.newDate,
        meeting_number: formData.newMeetingNumber || 1,
        location: formData.newLocation,
        attendees: formData.newAttendees,
        family_id: autoFamilyId,
        mentor_id: userId,
        details: formData.details, // ✅ שדה הערות נוסף כאן
      };

      const total_income = toNumber(financialData.total_income);
      const total_expenses = toNumber(financialData.total_expenses);
      const one_time_debt_repayment = toNumber(financialData.one_time_debt_repayment);
      const monthly_debt_repayment = toNumber(financialData.monthly_debt_repayment);
      const short_term_savings = toNumber(financialData.short_term_savings);
      const long_term_savings = toNumber(financialData.long_term_savings);

      const income_expense_difference = total_income - total_expenses;
      const difference_after_repayment = income_expense_difference - one_time_debt_repayment - monthly_debt_repayment;

      const financialDataToSend: FinancialData = {
        id: undefined as any,
        total_income,
        total_expenses,
        current_debt_amount: toNumber(financialData.current_debt_amount),
        one_time_debt_repayment,
        monthly_debt_repayment,
        short_term_savings,
        long_term_savings,
        income_expense_difference,
        difference_after_repayment,
      };

      const result = await createMeeting({
        meetingData,
        financialData: financialDataToSend,
      }).unwrap();

     await setSaveSuccess(true);
     await setMeetingID((result as any).data?.id);
    await setCurrentMeeting((result as any).data);

    } catch (error) {
      console.error("❌ Error saving meeting:", error);
    }
  };

  const isFormValid = !!formData.newDate;
  const total_income_num = toNumber(financialData.total_income);
  const total_expenses_num = toNumber(financialData.total_expenses);
  const short_term_savings_num = toNumber(financialData.short_term_savings);
  const long_term_savings_num = toNumber(financialData.long_term_savings);
  const income_expense_difference_num = total_income_num - total_expenses_num;
  const difference_after_repayment_num = income_expense_difference_num - toNumber(financialData.one_time_debt_repayment) - toNumber(financialData.monthly_debt_repayment);

  return (

    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-5xl mx-auto bg-secondary rounded-2xl shadow-card p-8 space-y-12">
        {/* Basic Info */}
        <section>
          <h2 className="text-3xl font-semibold text-text-main mb-6">נתונים על הפגישה:</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <input type="date" value={formData.newDate} onChange={(e) => handleBasicChange("newDate", e.target.value)} className="input" required />
            <input type="number" value={formData.newMeetingNumber === 0 ? "" : formData.newMeetingNumber.toString()} onChange={(e) => handleBasicChange("newMeetingNumber", Number(e.target.value))} className="input" min={1} />
<input type="date" value={formData.newDate} onChange={(e) => handleBasicChange("newDate", e.target.value)} className="input" placeholder="תאריך פגישה" required />
            <input type="text" value={formData.newLocation} onChange={(e) => handleBasicChange("newLocation", e.target.value)} className="input" placeholder="מיקום פגישה" />
            <input type="text" value={attendeesInput} onChange={(e) => { const raw = e.target.value; setAttendeesInput(raw); if (raw.includes(",")) tryUpdateAttendees(raw); }} onBlur={() => tryUpdateAttendees(attendeesInput)} className="input" placeholder="משתתפים (מופרדים בפסיקים)" />
          </div>
        </section>

        {/* Financial Info */}
        <section>
          <h2 className="text-3xl font-semibold text-text-main mb-6">נתונים פיננסיים:</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {financialFieldKeys.map((field) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{translateField(field)}</label>
                <input type="number" name={field} value={financialData[field] ?? "0"} onChange={handleFinancialChange} onFocus={handleFinancialFocus} onBlur={handleFinancialBlur} min={0} className="input" />
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-3xl font-semibold text-text-main mb-6">הערות נוספות:</h2>
          <textarea
            value={formData.details}
            onChange={(e) => handleBasicChange("details", e.target.value)}
            className="input h-32 w-full resize-none border border-black rounded-lg p-3"
            placeholder="הערות על הפגישה"
          />
        </section>

        <section className="bg-blue-light rounded-xl p-6 shadow-inner">
          <h3 className="text-2xl font-semibold mb-4 text-text-main">סיכום פיננסי:</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-sm text-gray-500">סה״כ הכנסות</div>
              <div className="text-lg font-bold text-success">{formatCurrency(total_income_num)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">סה״כ הוצאות</div>
              <div className="text-lg font-bold text-danger">{formatCurrency(total_expenses_num)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">סך חיסכון</div>
              <div className="text-lg font-bold text-primary">{formatCurrency(short_term_savings_num + long_term_savings_num)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">יתרה נטו</div>
              <div className={`text-lg font-bold ${difference_after_repayment_num >= 0 ? "text-success" : "text-danger"}`}>
                {formatCurrency(difference_after_repayment_num)}
              </div>
            </div>
          </div>
        </section>

        {formData.newFamilyId && <OpenTasksList familyId={formData.newFamilyId} />}

        {saveSuccess && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
           <label> ✅ הפגישה נשמרה בהצלחה! </label> 
            <PdfDownloader />
            <SendEmailButton meetingID={meetingID} />
            <NextMeetingForm currentMeeting={currentMeeting} />
          </div>
        )}

        <div className="flex justify-center">
          {saveSuccess ? (
            <button
              onClick={() => {
                setFormData({
                  newDate: "",
                  newMeetingNumber: 0,
                  newLocation: "",
                  newAttendees: [],
                  newFamilyId: autoFamilyId || family?.id || "",
                  newMentorId: userId || "",
                  details: "",
                });
                setAttendeesInput("");
                setFinancialData({
                  total_income: "0",
                  total_expenses: "0",
                  current_debt_amount: "0",
                  one_time_debt_repayment: "0",
                  monthly_debt_repayment: "0",
                  short_term_savings: "0",
                  long_term_savings: "0",
                });
                setSaveSuccess(false);
                setMeetingID('');
              }}
              className="px-8 py-3 rounded-lg font-semibold text-lg bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-xl transition-all"
            >
              🆕 צור פגישה חדשה
            </button>
          ) : (
            <button
              onClick={handleSaveMeeting}
              disabled={!isFormValid || saving}
              className={`px-8 py-3 rounded-lg font-semibold text-lg transition-all ${isFormValid && !saving
                  ? "bg-primary text-white hover:bg-primary-dark shadow-lg hover:shadow-xl"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
            >
              {saving ? "💾 שומר..." : "💾 שמור פגישה"}
            </button>
          )}
        </div>

        {!isFormValid && (
          <div className="text-center text-gray-600 text-sm">*נדרש להזין תאריך פגישה</div>
        )}

        {showPopup && (
          <PopupModal onClose={() => setShowPopup(false)}>
            <TaskTable />
          </PopupModal>
        )}

        <div className="flex justify-center">
          <button
            onClick={() => setShowPopup(true)}
            className="px-4 py-2 bg-secondary hover:bg-secondary-dark rounded-lg text-text-main font-semibold"
          >
            לניהול והוספת משימות
          </button>
        </div>
      </div>
    </div>
  );
};

function translateField(field: keyof FinancialData): string {
  const map: Record<keyof FinancialData, string> = {
    id: "מזהה",
    total_income: "סך הכנסות",
    total_expenses: "סך הוצאות",
    current_debt_amount: "סכום חוב נוכחי",
    income_expense_difference: "הפרש הכנסות/הוצאות",
    one_time_debt_repayment: "החזר חוב חד פעמי",
    monthly_debt_repayment: "החזר חוב חודשי",
    difference_after_repayment: "יתרה לאחר החזרת חובות",
    short_term_savings: "חסכון קצר טווח",
    long_term_savings: "חסכון ארוך טווח",
  };
  return map[field] || field;
}

export default MeetingForm;